/**
 * F-70: subscribe to the board doc's live translation-job state.
 *
 * The doc is the progress transport (schema `txJob`): the server's job loop
 * publishes each tick under its own origin, so this hook is why every client
 * shows the same bar, a reload mid-job still shows it, and nothing polls.
 *
 * READ-ONLY, by contract. Starting and cancelling go through the job API
 * (`POST`/`DELETE …/translate`); a client write here would put a progress tick
 * on that user's undo stack (C6/C8).
 */
import type { TranslationJobState } from "@kola/shared";
import { useEffect, useState } from "react";

import type { BoardSession } from "../../core/session.js";

export function useTranslationJob(session: BoardSession): TranslationJobState | undefined {
  const { board } = session;
  const [job, setJob] = useState<TranslationJobState | undefined>(() => board.getTranslationJob());

  useEffect(() => {
    // Re-read before subscribing: a tick that lands between this component's
    // render and this effect belongs to no observer, and the mount-time read
    // above would silently be stale forever after (the doc only notifies on
    // CHANGE, so nothing would ever correct it).
    setJob(board.getTranslationJob());
    return board.observeTranslationJob(setJob);
  }, [board]);

  return job;
}
